'use client'

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

/**
 * Stadium trip planner for out-of-town readers: pick where you start and whether
 * the event is in the afternoon or evening, get a leave-by time and the trip-home
 * advice, then hand off to the route page to pick dates. Deliberately not tied to
 * one event, so it never goes stale. A dock pins to the bottom of the screen once
 * the planner scrolls out of view and brings the reader back to it.
 *
 * The controls explain themselves, so there is no helper copy around them: the
 * intro line is optional and the button carries no note underneath.
 */

export interface PlannerAdvice {
    /** A short verdict above the advice. Leave it out when the body leads with the departure. */
    head?: string
    body: string
}

export interface PlannerCity {
    key: string
    label: string
    /** How long the ride takes and by what, e.g. "About 4h 30m by bus". */
    travel: string
    /** Door to hub, in hours, for the leave-by time. */
    rideHours: number
    /** Route page into the hub city; the reader picks dates there. */
    href: string
    /** Live "from" fare for that route, when there is one. */
    fare?: string | null
    /** Trip-home advice after an evening event. */
    eveningHome: PlannerAdvice
    /** Trip-home advice after an afternoon event, when this city differs from the default. */
    afternoonHome?: PlannerAdvice
    /** The last leg from where this city's buses and trains arrive, when it differs from the default. */
    lastStretch?: ReactNode
    /** The route page back home (hub city to this city), when Wanderu sells it. */
    homeHref?: string
    /** Label for the home link, when the route page sells only one mode (e.g. "Buses home"). */
    homeLabel?: string
    /**
     * Hours this city's clock runs ahead of the hub's (1 for Eastern time against a
     * Central-time hub), so the leave-by time reads on the reader's own clock.
     */
    clockOffsetHours?: number
    /** Names the clock when it differs from the hub's, e.g. "Eastern time". */
    clockLabel?: string
    /**
     * The bus to take, by event slot, in place of the computed leave-by time. Use it
     * when the hub's sold departures, not the ride time, decide the answer (for
     * example "Arrive the day before" when no bus makes the gates). A slot set to
     * `null` shows the ride time alone, for a city whose departures for that slot
     * are too thin to name one.
     */
    go?: { afternoon?: ReactNode; evening?: ReactNode }
}

export interface TripPlannerProps {
    title: string
    intro?: string
    cities: readonly PlannerCity[]
    /** Label for the "not listed" option, e.g. "Somewhere else". */
    otherLabel: string
    /** Placeholder for the free-text city field. Leave it out to show no field. */
    otherPlaceholder?: string
    /** Words after the planner's fare chip, e.g. "one way". */
    fareSuffix?: string
    /** Where "Somewhere else" hands off, e.g. the hub city's bus page. */
    otherHref: string
    /**
     * Where buses and trains from the listed cities arrive, e.g. "Midtown". Leave it
     * out when each city's `travel` line already reads as a whole sentence.
     */
    arrival?: string
    goHead: string
    /** Getting-there advice when the starting city is not in the list. */
    otherGoBody: string
    afternoon: { startHour: number; startLabel: string; home: PlannerAdvice }
    evening: { startHour: number; startLabel: string; fallbackHome: PlannerAdvice }
    /** One line on the last leg, used unless the city carries its own. */
    lastStretch: ReactNode
    /** Optional line under the button, e.g. a pointer to the events block. */
    note?: ReactNode
    dockHead: string
    dockSub: string
}

const OTHER = 'other'

/** Hours after midnight to a "3:30pm" label, rounded to the quarter hour. */
function formatTime(hours: number): string {
    let h = Math.floor(hours)
    let m = Math.round(((hours - h) * 60) / 15) * 15
    if (m === 60) {
        h += 1
        m = 0
    }
    const suffix = h >= 12 && h < 24 ? 'pm' : 'am'
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}${suffix}`
}

export function TripPlanner(props: TripPlannerProps) {
    const { cities } = props
    const [cityKey, setCityKey] = useState(cities[0]?.key ?? OTHER)
    const [when, setWhen] = useState<'afternoon' | 'evening'>('evening')
    const [otherCity, setOtherCity] = useState('')
    const [showDock, setShowDock] = useState(false)
    const plannerRef = useRef<HTMLDivElement>(null)
    const id = useId()

    useEffect(() => {
        const node = plannerRef.current
        if (!node || typeof IntersectionObserver === 'undefined') {
            return
        }
        const observer = new IntersectionObserver(([entry]) => {
            // Only once the planner has scrolled up past the top, not before it.
            setShowDock(!entry.isIntersecting && entry.boundingClientRect.top < 0)
        })
        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    const city = cities.find((c) => c.key === cityKey)
    const event = when === 'afternoon' ? props.afternoon : props.evening
    const travel = city ? `${city.travel}${props.arrival ? ` to ${props.arrival}` : ''}.` : ''
    const go = city?.go
    const goOverride: ReactNode | undefined = go && when in go ? (go[when] ?? null) : undefined
    const goBody: ReactNode =
        city && goOverride !== undefined ? (
            goOverride === null ? (
                travel
            ) : (
                <>
                    {travel} {goOverride}
                </>
            )
        ) : city ? (
            `${travel} For ${event.startLabel}, leave ${city.label} by about ${formatTime(event.startHour - 2 - city.rideHours + (city.clockOffsetHours ?? 0))}${city.clockLabel ? ` ${city.clockLabel}` : ''}.`
        ) : (
            props.otherGoBody
        )
    const home =
        when === 'afternoon'
            ? (city?.afternoonHome ?? props.afternoon.home)
            : (city?.eveningHome ?? props.evening.fallbackHome)
    const href = city?.href ?? props.otherHref
    const lastStretch = city?.lastStretch ?? props.lastStretch

    const toPlanner = () => {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        plannerRef.current?.scrollIntoView({
            behavior: reduced ? 'auto' : 'smooth',
            block: 'start',
        })
    }

    return (
        <>
            <div
                ref={plannerRef}
                id="trip-planner"
                data-testid="trip-planner"
                className="mt-8 scroll-mt-16 rounded-[8px] bg-[var(--wanderblu-50)] p-[18px]"
            >
                <p className="mb-1 text-[20px] leading-tight font-bold text-[var(--smoke-950)]">
                    {props.title}
                </p>
                {props.intro ? (
                    <p className="mb-3.5 text-[16px] leading-snug text-pretty">{props.intro}</p>
                ) : (
                    <div className="mb-3" />
                )}

                <div className="grid grid-cols-1 items-end gap-2.5 sm:grid-cols-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-[14px] font-semibold">I&rsquo;m starting from</span>
                        <select
                            value={cityKey}
                            onChange={(e) => setCityKey(e.target.value)}
                            className="h-11 rounded-[3px] border border-[var(--smoke-500)] bg-white px-2.5 text-[16px] font-semibold text-[var(--smoke-950)]"
                        >
                            {cities.map((c) => (
                                <option key={c.key} value={c.key}>
                                    {c.label}
                                </option>
                            ))}
                            <option value={OTHER}>{props.otherLabel}</option>
                        </select>
                    </label>
                    <div className="flex flex-col gap-1">
                        <span id={`${id}-when`} className="text-[14px] font-semibold">
                            My event starts
                        </span>
                        <div
                            role="group"
                            aria-labelledby={`${id}-when`}
                            className="flex h-11 overflow-hidden rounded-[3px] border border-[var(--smoke-500)] bg-white"
                        >
                            {(
                                [
                                    ['afternoon', 'Afternoon'],
                                    ['evening', 'Evening'],
                                ] as const
                            ).map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    aria-pressed={when === key}
                                    onClick={() => setWhen(key)}
                                    className={
                                        when === key
                                            ? 'flex-1 bg-[var(--wanderblu-700)] text-[15px] font-semibold text-white'
                                            : 'flex-1 bg-white text-[15px] font-semibold text-[var(--smoke-950)]'
                                    }
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {cityKey === OTHER && props.otherPlaceholder ? (
                    <label className="mt-2.5 flex flex-col gap-1">
                        <span className="text-[14px] font-semibold">Your city</span>
                        <input
                            value={otherCity}
                            onChange={(e) => setOtherCity(e.target.value)}
                            placeholder={props.otherPlaceholder}
                            className="h-11 w-full rounded-[3px] border border-[var(--smoke-500)] bg-white px-3 text-[16px] font-semibold text-[var(--smoke-950)]"
                        />
                    </label>
                ) : null}

                <div aria-live="polite" className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5 rounded-[3px] bg-white p-3.5">
                        <span className="text-[14px] font-semibold text-[var(--smoke-800)]">
                            Getting there
                        </span>
                        <span className="text-[18px] leading-snug font-bold">{props.goHead}</span>
                        <span className="text-[16px] leading-snug text-pretty">{goBody}</span>
                        {city?.fare ? (
                            <span
                                data-testid="planner-fare"
                                className="self-start rounded-[2px] bg-[var(--wanderblu-50)] px-2 py-0.5 text-[14px] font-bold text-[var(--wanderblu-900)]"
                            >
                                from {city.fare}
                                {props.fareSuffix ? ` ${props.fareSuffix}` : null}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex flex-col gap-1.5 rounded-[3px] bg-white p-3.5">
                        <span className="text-[14px] font-semibold text-[var(--smoke-800)]">
                            Getting home
                        </span>
                        {home.head ? (
                            <span className="text-[18px] leading-snug font-bold">{home.head}</span>
                        ) : null}
                        <span className="text-[16px] leading-snug text-pretty">{home.body}</span>
                        {city?.homeHref ? (
                            <a
                                href={city.homeHref}
                                className="self-start text-[15px] font-semibold text-[var(--wanderblu-700)] underline underline-offset-2"
                            >
                                {city.homeLabel ?? 'Buses and trains home'}
                            </a>
                        ) : null}
                    </div>
                </div>

                <p className="mt-2 mb-0 text-[15px] leading-snug text-pretty">{lastStretch}</p>
                <a
                    href={href}
                    className="mt-3 flex h-12 w-full items-center justify-center rounded-[3px] bg-[var(--watermelon-700)] text-[17px] font-bold text-white no-underline transition-colors hover:brightness-90"
                >
                    Search Tickets
                </a>
                {props.note ? (
                    <p className="mt-2 mb-0 text-center text-[14px] text-pretty">{props.note}</p>
                ) : null}
            </div>

            {showDock ? (
                <div
                    data-testid="planner-dock"
                    className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--smoke-300)] bg-white shadow-[0_-2px_6px_rgba(46,48,51,0.08)]"
                >
                    <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-2.5">
                        <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-[16px] font-bold">{props.dockHead}</span>
                            <span className="truncate text-[14px] text-[var(--smoke-800)]">
                                {props.dockSub}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={toPlanner}
                            className="h-11 flex-none rounded-[3px] bg-[var(--wanderblu-700)] px-[18px] text-[16px] font-bold text-white transition-colors hover:bg-[var(--wanderblu-800)]"
                        >
                            Search Tickets
                        </button>
                    </div>
                </div>
            ) : null}
        </>
    )
}
